import { z } from 'zod';
import { IdSchema, LinkRecordSchema, ObjectRecordSchema } from './records';
import { PrimitiveListSchema } from './primitives';
import { PermissionSchema, type Requirement } from './manifest';
import { LocalizedStringSchema } from './i18n';
import { CARD_LIMITS, CardInputSchema, CardLookSchema, CardStateSchema, CardUseSchema } from './profile-card';

const CardId = z.string().regex(/^[a-z][a-z0-9-]{0,31}$/);
const CardSize = z.object({ w: z.number().min(120).max(760), h: z.number().min(84).max(483) }).strict();

/** Version of the message envelope (independent from API semver). */
export const PROTOCOL_VERSION = 1;

export const EnvelopeSchema = z.discriminatedUnion('kind', [
  z.object({ v: z.literal(1), id: z.string().max(64), kind: z.literal('req'), method: z.string().max(64), params: z.unknown() }),
  z.object({ v: z.literal(1), id: z.string().max(64), kind: z.literal('res'), result: z.unknown() }),
  z.object({
    v: z.literal(1),
    id: z.string().max(64),
    kind: z.literal('err'),
    error: z.object({ code: z.string().max(64), message: z.string().max(2000) }),
  }),
  z.object({ v: z.literal(1), id: z.string().max(64), kind: z.literal('evt'), method: z.string().max(64), params: z.unknown() }),
  z.object({ v: z.literal(1), id: z.string().max(64), kind: z.literal('cancel') }),
]);
export type Envelope = z.infer<typeof EnvelopeSchema>;

const Obj = ObjectRecordSchema;
/**
 * API 2.2: several objects with arrows between them, created in one step (`objects.createTree`,
 * template files). `ref` names a new object so arrows can point at it; an arrow end is a ref or the
 * id of an existing object. Frames listed before objects take the objects they cover.
 */
export const TreeObjectSchema = z.object({
  ref: z.string().regex(/^[\w.-]{1,64}$/).optional(),
  type: z.string(),
  x: z.number().finite(),
  y: z.number().finite(),
  w: z.number().finite().min(0),
  h: z.number().finite().min(0),
  props: z.record(z.unknown()).optional(),
  style: z.record(z.unknown()).optional(),
  name: z.string().max(500).optional(),
});
export const TreeConnectorSchema = z.object({ from: z.string().max(64), to: z.string().max(64), label: z.string().max(500).optional(), arrow: z.boolean().optional() });
export const TreeSpecSchema = z.object({ objects: z.array(TreeObjectSchema).min(1).max(5000), connectors: z.array(TreeConnectorSchema).max(5000).optional() });
export type TreeSpec = z.infer<typeof TreeSpecSchema>;

const Ref = z.string().regex(/^[\w.-]{1,64}$/);
/**
 * API 2.6: an object of a new canvas (`canvases.create`). The list order is the layer order, bottom
 * to top. `group` — ref of the `core:group` the object belongs to (the group's box is computed from
 * its members); `frame` — ref of a `core:frame` earlier in the list that holds the object (for a
 * group member it holds the whole group); `image` — ref of an image of the spec shown by a
 * `core:image`; `link` — a web address the object leads to. A `core:connector` glues its ends to
 * objects of the list with `props.start.ref` / `props.end.ref` and is drawn along `props.path`.
 */
export const CanvasObjectSchema = TreeObjectSchema.extend({
  rotation: z.number().finite().optional(),
  locked: z.boolean().optional(),
  group: Ref.optional(),
  frame: Ref.optional(),
  image: Ref.optional(),
  link: z.string().max(2000).optional(),
});
export const CanvasSpecSchema = z.object({
  name: z.string().min(1).max(500),
  objects: z.array(CanvasObjectSchema).max(50_000),
  /** Pictures for `core:image` objects: PNG, JPEG, GIF, WebP or SVG (turned into a picture), up to 20 MB each. */
  images: z.array(z.object({ ref: Ref, data: z.instanceof(Uint8Array) })).max(2000).optional(),
});
export type CanvasObjectSpec = z.infer<typeof CanvasObjectSchema>;
export type CanvasSpec = z.infer<typeof CanvasSpecSchema>;

/** API 2.2: `canvas` — data of the open canvas (travels with it), `app` — the module's own, across canvases. */
const StorageScope = z.enum(['canvas', 'app']);
const StorageKey = z.string().regex(/^[\w.:/-]{1,200}$/);
const Size = z.object({ w: z.number().positive(), h: z.number().positive() });

export const ObjectPatchSchema = z
  .object({
    x: z.number().finite().optional(),
    y: z.number().finite().optional(),
    w: z.number().finite().min(0).optional(),
    h: z.number().finite().min(0).optional(),
    rotation: z.number().finite().optional(),
    name: z.string().max(500).optional(),
    props: z.record(z.unknown()).optional(),
    style: z.record(z.unknown()).optional(),
  })
  .strict();
export type ObjectPatch = z.infer<typeof ObjectPatchSchema>;

export interface MethodDef<P extends z.ZodTypeAny = z.ZodTypeAny, R extends z.ZodTypeAny = z.ZodTypeAny> {
  params: P;
  result: R;
  /** Required permission for module → host calls. */
  permission?: Requirement;
}
function def<P extends z.ZodTypeAny, R extends z.ZodTypeAny>(params: P, result: R, permission?: Requirement): MethodDef<P, R> {
  return { params, result, permission };
}

/** Host → module requests. */
export const ModuleMethods = {
  activate: def(
    z.object({
      moduleId: z.string(),
      apiVersion: z.string(),
      locale: z.string(),
      permissions: z.array(PermissionSchema),
      /** Bundled ES module source of the entry point. */
      code: z.string(),
      /** locale → key → text from locales/*.json. */
      messages: z.record(z.record(z.string())),
    }),
    z.object({ ok: z.literal(true) }),
  ),
  render: def(
    z.object({ type: z.string(), object: Obj, scale: z.number().positive() }),
    z.object({ primitives: PrimitiveListSchema }),
  ),
  renderRaster: def(
    z.object({
      type: z.string(),
      object: Obj,
      scale: z.number().positive(),
      width: z.number().int().positive().max(8192),
      height: z.number().int().positive().max(8192),
    }),
    z.object({ bitmap: z.unknown() }),
  ),
  compute: def(
    z.object({
      type: z.string(),
      object: Obj,
      inputs: z.array(z.object({ link: LinkRecordSchema, source: Obj.nullable() })),
    }),
    z.object({ patch: ObjectPatchSchema.nullable() }),
  ),
  migrate: def(
    z.object({ type: z.string(), object: Obj, fromVersion: z.number().int(), toVersion: z.number().int() }),
    z.object({ object: Obj }),
  ),
  createDefaults: def(z.object({ type: z.string(), size: Size }), z.object({ props: z.record(z.unknown()) })),
  command: def(z.object({ commandId: z.string(), selection: z.array(IdSchema) }), z.object({}).passthrough()),
  /** Plain text of an object for Find on canvas; null when the module does not provide it. */
  searchText: def(z.object({ type: z.string(), object: Obj }), z.object({ text: z.string().max(1_000_000).nullable() })),
  /** API 2.2: the gesture of a module tool (`tools[].gesture`), world points. */
  toolGesture: def(
    z.object({ toolId: z.string(), kind: z.enum(['click', 'line', 'path']), points: z.array(z.object({ x: z.number().finite(), y: z.number().finite() })).min(1).max(20000) }),
    z.object({ ids: z.array(IdSchema).max(100_000).optional() }).passthrough(),
  ),
  /** API 2.2: files the user picked for an import format of the module. */
  importFile: def(
    z.object({ formatId: z.string(), files: z.array(z.object({ name: z.string(), type: z.string(), size: z.number(), data: z.instanceof(Uint8Array) })) }),
    z.object({ ids: z.array(IdSchema).max(100_000).optional() }).passthrough(),
  ),
  /** API 2.2: the selection (empty: the whole canvas) as a file of an export format of the module. */
  exportFile: def(
    z.object({ formatId: z.string(), selection: z.array(IdSchema) }),
    z.object({ name: z.string().min(1).max(200), data: z.union([z.string(), z.instanceof(Uint8Array)]), type: z.string().max(100).optional() }),
  ),
  /** API 1.6: a tool of this module called by an AI gateway module. */
  mcpTool: def(z.object({ name: z.string(), args: z.record(z.unknown()), caller: z.string() }), z.object({ result: z.unknown() })),
  /** Mode C: html for a live object view; panel: html for an advanced properties section. */
  view: def(
    z.object({ type: z.string(), kind: z.enum(['object', 'panel']), objectId: IdSchema }),
    z.object({ html: z.string().max(2_000_000) }),
  ),
} as const;

/** Module → host requests. */
export const HostMethods = {
  'objects.get': def(z.object({ ids: z.array(IdSchema).max(10000) }), z.object({ objects: z.array(Obj) }), 'canvas.read'),
  'objects.query': def(
    /** `text` (API 1.6): only objects whose text, name, label or title contains it (any case). */
    z.object({ types: z.array(z.string()).max(100).optional(), limit: z.number().int().positive().max(10000).optional(), text: z.string().max(500).optional() }),
    z.object({ objects: z.array(Obj) }),
    'canvas.read',
  ),
  'objects.create': def(
    z.object({
      objects: z
        .array(
          z.object({
            type: z.string(),
            x: z.number().finite(),
            y: z.number().finite(),
            w: z.number().finite().min(0),
            h: z.number().finite().min(0),
            props: z.record(z.unknown()).optional(),
            /** API 1.6 */
            style: z.record(z.unknown()).optional(),
            name: z.string().max(500).optional(),
          }),
        )
        .max(5000),
    }),
    z.object({ ids: z.array(IdSchema) }),
    'canvas.write',
  ),
  'objects.update': def(
    z.object({ updates: z.array(z.object({ id: IdSchema, patch: ObjectPatchSchema })).max(10000) }),
    z.object({}),
    'canvas.write',
  ),
  'objects.delete': def(z.object({ ids: z.array(IdSchema).max(10000) }), z.object({}), 'canvas.write'),
  'links.get': def(z.object({ objectId: IdSchema }), z.object({ links: z.array(LinkRecordSchema) }), 'canvas.read'),
  'links.create': def(
    z.object({ type: z.string(), from: IdSchema, to: IdSchema, props: z.record(z.unknown()).optional() }),
    z.object({ id: IdSchema }),
    'canvas.write',
  ),
  'links.delete': def(z.object({ ids: z.array(IdSchema).max(10000) }), z.object({}), 'canvas.write'),
  'ui.notify': def(
    z.object({
      message: z.string().max(2000),
      level: z.enum(['info', 'warning', 'error']),
      objectId: IdSchema.optional(),
      /** API 2.2: up to 3 buttons on the message, each runs a command of the module. */
      actions: z.array(z.object({ label: z.string().min(1).max(40), command: z.string().max(64) })).max(3).optional(),
    }),
    z.object({}),
  ),
  log: def(
    z.object({ level: z.enum(['debug', 'info', 'warn', 'error']), code: z.string().max(100), data: z.record(z.unknown()).optional() }),
    z.object({}),
  ),
  /** Fonts a module can use: the platform's and its own (no permission needed). */
  'fonts.list': def(z.object({}), z.object({ fonts: z.array(z.object({ family: z.string(), css: z.string(), source: z.enum(['platform', 'module']) })) })),
  /** Font faces with embedded data (data: URLs), for the module's own measuring and for its views. */
  'fonts.faces': def(
    z.object({ families: z.array(z.string().max(64)).max(32) }),
    z.object({ faces: z.array(z.object({ family: z.string(), weight: z.number(), style: z.enum(['normal', 'italic']), unicodeRange: z.string().optional(), url: z.string() })) }),
  ),
  /** DSkhema schemes (docs/ru/DSKHEMA.md §5.1). */
  'diagrams.validate': def(
    z.object({ source: z.string().max(500_000) }),
    z.object({
      diagnostics: z.array(
        z.object({ line: z.number(), col: z.number(), end: z.number(), severity: z.enum(['error', 'warning']), code: z.string(), message: z.string(), fix: z.object({ line: z.number(), col: z.number(), end: z.number(), text: z.string() }).optional() }),
      ),
    }),
  ),
  'diagrams.create': def(
    z.object({ source: z.string().max(500_000), x: z.number().finite().optional(), y: z.number().finite().optional() }),
    z.object({ id: IdSchema }),
    'canvas.write',
  ),
  'diagrams.update': def(z.object({ id: IdSchema, source: z.string().max(500_000) }), z.object({}), 'canvas.write'),
  // ---- API 1.6
  /** Values of the module's own settings (manifest `contributes.settings`). */
  'settings.get': def(z.object({}), z.object({ values: z.record(z.unknown()) })),
  /** The open canvas: id, name, number of objects, bounds of the content (null when empty). */
  'canvas.info': def(
    z.object({}),
    z.object({ id: z.string().nullable(), name: z.string(), objects: z.number(), bounds: z.object({ x: z.number(), y: z.number(), w: z.number(), h: z.number() }).nullable() }),
    'canvas.read',
  ),
  'selection.get': def(z.object({}), z.object({ ids: z.array(IdSchema) }), 'canvas.read'),
  'selection.set': def(z.object({ ids: z.array(IdSchema).max(10000) }), z.object({}), 'canvas.read'),
  /** Show objects on screen (the camera fits them). */
  'viewport.focus': def(z.object({ ids: z.array(IdSchema).max(10000) }), z.object({}), 'canvas.read'),
  /** A platform arrow between two objects, routed like one drawn by hand. */
  'connectors.create': def(
    z.object({ from: IdSchema, to: IdSchema, label: z.string().max(500).optional(), arrow: z.boolean().optional() }),
    z.object({ id: IdSchema }),
    'canvas.write',
  ),
  /** Tools other modules give to AI gateways (manifest `contributes.mcpTools`). */
  'modules.tools': def(
    z.object({}),
    z.object({ tools: z.array(z.object({ moduleId: z.string(), name: z.string(), description: z.string(), inputSchema: z.record(z.unknown()).optional() })) }),
    'mcp',
  ),
  /** Call a tool of another module. */
  'modules.call': def(z.object({ moduleId: z.string().max(128), name: z.string().max(64), args: z.record(z.unknown()).optional() }), z.object({ result: z.unknown() }), 'mcp'),
  // ---- API 2.0 (on a user action only, see the platform's gesture rule)
  'files.open': def(
    z.object({ accept: z.array(z.string().max(100)).max(20).optional(), multiple: z.boolean().optional() }),
    z.object({ files: z.array(z.object({ name: z.string(), type: z.string(), size: z.number(), data: z.instanceof(Uint8Array) })) }),
    'files',
  ),
  'files.save': def(z.object({ name: z.string().min(1).max(200), data: z.union([z.string(), z.instanceof(Uint8Array)]), type: z.string().max(100).optional() }), z.object({ saved: z.boolean() }), 'files'),
  'clipboard.readText': def(z.object({}), z.object({ text: z.string() }), 'clipboard'),
  'clipboard.writeText': def(z.object({ text: z.string().max(10_000_000) }), z.object({}), 'clipboard'),
  /**
   * API 2.2: keep an image (PNG, JPEG, GIF, WebP; up to 20 MB) as an attachment of the canvas; put the
   * hash into a `core:image` object (`props.attachment`) or an image primitive (`src: 'att:<hash>'`).
   */
  'attachments.put': def(
    z.object({ data: z.instanceof(Uint8Array) }),
    z.object({ hash: z.string(), type: z.string(), width: z.number().nullable(), height: z.number().nullable() }),
    'canvas.write',
  ),
  /** API 2.2: objects and arrows in one step (one undo step); `refs` — ref → id of the new objects. */
  'objects.createTree': def(TreeSpecSchema, z.object({ ids: z.array(IdSchema), refs: z.record(IdSchema) }), 'canvas.write'),
  // ---- API 2.2: the module's own storage (no permission: a module sees only its own data)
  'storage.get': def(z.object({ scope: StorageScope, key: StorageKey }), z.object({ value: z.unknown() })),
  'storage.set': def(z.object({ scope: StorageScope, key: StorageKey, value: z.unknown() }), z.object({})),
  'storage.delete': def(z.object({ scope: StorageScope, key: StorageKey }), z.object({})),
  'storage.list': def(z.object({ scope: StorageScope, prefix: z.string().max(200).optional() }), z.object({ keys: z.array(z.string()) })),
  // ---- API 2.3: windows (no permission: sandboxed like views; opened on a user action only)
  'windows.open': def(
    z.object({
      /** A window of the manifest (`contributes.windows[].id`)… */
      window: z.string().max(40).optional(),
      /** …or a document made at run time. */
      html: z.string().max(1_000_000).optional(),
      title: z.string().max(120).optional(),
      width: z.number().int().min(200).max(1600).optional(),
      height: z.number().int().min(120).max(1200).optional(),
      resizable: z.boolean().optional(),
      modal: z.boolean().optional(),
      /** Delivered to the window as its first message. */
      data: z.unknown().optional(),
    }),
    z.object({ handle: z.string() }),
  ),
  'windows.close': def(z.object({ handle: z.string().max(64) }), z.object({})),
  'windows.setTitle': def(z.object({ handle: z.string().max(64), title: z.string().max(120) }), z.object({})),
  // ---- API 2.6: new canvases (converters, importers); the open canvas is not touched
  'canvases.create': def(CanvasSpecSchema, z.object({ id: IdSchema, objects: z.number().int() }), 'canvas.create'),
  /** Open a canvas this module made (since it started); on a user action only. */
  'canvases.open': def(z.object({ id: IdSchema }), z.object({}), 'canvas.create'),
  // ---- API 4.10: profile cards made at run time (`profile.cards`); state and look of any own card
  'profileCards.create': def(
    z.object({ title: LocalizedStringSchema, html: z.string().max(CARD_LIMITS.html), size: CardSize.optional(), look: CardLookSchema.optional(), uses: z.array(CardUseSchema).max(30).default([]), show: z.boolean().optional() }),
    z.object({ id: CardId }),
    'profile.cards',
  ),
  'profileCards.update': def(
    z.object({ id: CardId, title: LocalizedStringSchema.optional(), html: z.string().max(CARD_LIMITS.html).optional(), uses: z.array(CardUseSchema).max(30).optional() }),
    z.object({}),
    'profile.cards',
  ),
  'profileCards.remove': def(z.object({ id: CardId }), z.object({}), 'profile.cards'),
  'profileCards.list': def(z.object({}), z.object({ cards: z.array(z.object({ id: CardId, title: LocalizedStringSchema })) }), 'profile.cards'),
  'profileCards.setState': def(z.object({ card: CardId, values: CardStateSchema }), z.object({})),
  'profileCards.getState': def(z.object({ card: CardId }), z.object({ values: z.record(z.unknown()) })),
  'profileCards.setLook': def(z.object({ card: CardId, look: CardLookSchema.nullable() }), z.object({})),
} as const;

/** Host → module events (fire and forget). */
export const ModuleEvents = {
  'objects.changed': z.object({ ids: z.array(IdSchema) }),
  'selection.changed': z.object({ ids: z.array(IdSchema) }),
  'locale.changed': z.object({ locale: z.string() }),
  'permissions.changed': z.object({ permissions: z.array(PermissionSchema) }),
  /** Messages from a mode C view / panel iframe forwarded to the worker. */
  'view.message': z.object({ objectId: IdSchema, data: z.unknown() }),
  /** API 1.6: the user changed the module's settings. */
  'settings.changed': z.object({ values: z.record(z.unknown()) }),
  /** API 2.2 (canvas.read): a canvas was opened (became the current one) / closed. */
  'canvas.opened': z.object({ id: z.string(), name: z.string() }),
  'canvas.closed': z.object({ id: z.string() }),
  /** API 2.2 (canvas.read, on subscription): objects of any type changed on the open canvas. */
  'canvas.changed': z.object({ ids: z.array(IdSchema) }),
  /** API 2.2: the user clicked a clickable area (primitive `action`) of an object of the module. */
  'object.action': z.object({ objectId: IdSchema, action: z.object({ id: z.string(), data: z.unknown().optional() }) }),
  /** API 2.0: a message from the module's panel; `opened`/`closed` when the user opens or closes it. */
  'panel.message': z.object({ data: z.unknown() }),
  /** API 2.3: a message from a window of the module (`window` — its manifest id, null for a run-time window). */
  'window.message': z.object({ handle: z.string(), window: z.string().nullable(), data: z.unknown() }),
  /** API 2.3: a window of the module was closed (by the user, the module or the platform). */
  'window.closed': z.object({ handle: z.string(), window: z.string().nullable() }),
  /** API 3.0: a message from a profile card of the module (`card` — its manifest id); `opened`/`closed` too. */
  'profileCard.message': z.object({ card: z.string(), data: z.unknown() }),
  /** API 4.10: the person used an interactive element of a card of the module (`data-vp-action`, `-toggle`, `-counter`, `-note`). */
  'profileCard.input': z.object({ card: z.string(), input: CardInputSchema }),
  /**
   * API 4.6 (keyboard): a key of the user while one object of a type with `capabilities.keyboard` is
   * selected. Escape and combinations with Ctrl or Cmd stay with the platform.
   */
  key: z.object({ objectId: IdSchema, type: z.enum(['down', 'up']), key: z.string().max(40), code: z.string().max(40), shift: z.boolean(), alt: z.boolean(), repeat: z.boolean() }),
} as const;

/** Module → host events. */
export const HostEvents = {
  ready: z.object({}),
  /** Ask the host to re-render given objects (state changed inside module). */
  invalidate: z.object({ ids: z.array(IdSchema).max(10000) }),
  /** Messages for a mode C view iframe. */
  'view.post': z.object({ objectId: IdSchema, data: z.unknown() }),
  /** API 2.2: the module listens to an event that is sent only to listeners (`canvas.changed`). */
  subscribe: z.object({ event: z.enum(['canvas.changed']) }),
  /** API 2.0: a message for the module's panel. */
  'panel.post': z.object({ data: z.unknown() }),
  /** API 2.3: a message for a window of the module. */
  'window.post': z.object({ handle: z.string().max(64), data: z.unknown() }),
  /** API 3.0: a message for a profile card of the module (shown only while the card is on screen). */
  'profileCard.post': z.object({ card: z.string().max(32), data: z.unknown() }),
} as const;

export type ModuleMethodName = keyof typeof ModuleMethods;
export type HostMethodName = keyof typeof HostMethods;
export type ModuleEventName = keyof typeof ModuleEvents;
export type HostEventName = keyof typeof HostEvents;
export type ModuleMethodParams<M extends ModuleMethodName> = z.infer<(typeof ModuleMethods)[M]['params']>;
export type ModuleMethodResult<M extends ModuleMethodName> = z.infer<(typeof ModuleMethods)[M]['result']>;
export type HostMethodParams<M extends HostMethodName> = z.infer<(typeof HostMethods)[M]['params']>;
export type HostMethodResult<M extends HostMethodName> = z.infer<(typeof HostMethods)[M]['result']>;

export const ErrorCodes = {
  invalidMessage: 'E_INVALID_MESSAGE',
  unknownMethod: 'E_UNKNOWN_METHOD',
  invalidParams: 'E_INVALID_PARAMS',
  invalidResult: 'E_INVALID_RESULT',
  permissionDenied: 'E_PERMISSION_DENIED',
  timeout: 'E_TIMEOUT',
  cancelled: 'E_CANCELLED',
  conflict: 'E_CONFLICT',
  notFound: 'E_NOT_FOUND',
  forbidden: 'E_FORBIDDEN',
  internal: 'E_INTERNAL',
  moduleError: 'E_MODULE',
} as const;
